import { Card, Collapse, Descriptions, Empty, Space, Table, Tag, Typography } from 'antd'
import type { TableProps } from 'antd'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import type { ITeacherDemoDiagnosticsBatch } from '~/types/teacher-application'
import {
  buildDemoNetworkSummary,
  isWarningSample,
  sampleJitterMs,
  samplePacketLossRatio,
  sampleRttMs,
} from './demoNetworkDiagnostics'

const qualityLabels = ['未知', '优秀', '良好', '较差', '差', '非常差', '连接中断']
const connectionLabels: Record<string, string> = {
  CONNECTED: '已连接', CONNECTING: '连接中', RECONNECTING: '重连中',
  DISCONNECTING: '正在断开', DISCONNECTED: '已断开',
}
const measurement = (value: number | null | undefined, unit: string, scale = 1) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? `${(value * scale).toFixed(1)} ${unit}` : '—'
const quality = (value?: number | null) => qualityLabels[value ?? 0] ?? '未知'
const formatTime = (value: number) => dayjs(value).format('YYYY-MM-DD HH:mm:ss')
const elapsedTime = (at: number, startedAt?: number) => {
  if (startedAt == null || !Number.isFinite(startedAt)) return null
  if (at < startedAt) return '开始前'
  const seconds = Math.floor((at - startedAt) / 1000)
  return `开始后 ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

interface Props {
  batches?: ITeacherDemoDiagnosticsBatch[] | null
  startedAt?: number
}

export function DemoNetworkDiagnosticsCard({ batches, startedAt }: Props) {
  const summary = useMemo(() => buildDemoNetworkSummary(batches), [batches])
  const columns: TableProps<(typeof summary.samples)[number]>['columns'] = [
    {
      title: '采样时间', key: 'at', width: 170,
      render: (_, sample) => <Space direction="vertical" size={0}>
        <Typography.Text>{formatTime(sample.at)}</Typography.Text>
        <Typography.Text type="secondary">{elapsedTime(sample.at, startedAt)}</Typography.Text>
      </Space>,
    },
    {
      title: '连接状态', key: 'connection', width: 110,
      render: (_, sample) => <Space direction="vertical" size={0}>
        <span>{connectionLabels[sample.connection] ?? '未知'}</span>
        {isWarningSample(sample) && <Tag color="orange">波动</Tag>}
      </Space>,
    },
    {
      title: '上行 / 下行质量', key: 'quality', width: 140,
      render: (_, sample) => `${quality(sample.uplinkQuality)} / ${quality(sample.downlinkQuality)}`,
    },
    { title: '往返延迟（RTT）', key: 'rtt', width: 140, render: (_, sample) => measurement(sampleRttMs(sample), 'ms') },
    { title: '最高抖动', key: 'jitter', width: 100, render: (_, sample) => measurement(sampleJitterMs(sample), 'ms') },
    { title: '最高丢包率', key: 'loss', width: 110, render: (_, sample) => measurement(samplePacketLossRatio(sample), '%', 100) },
    {
      title: '发送 / 接收码率', key: 'bitrate', width: 180,
      render: (_, sample) => `${measurement(sample.sendBitrateBps, 'kbps', 0.001)} / ${measurement(sample.receiveBitrateBps, 'kbps', 0.001)}`,
    },
  ]

  return <Card title="Demo 课堂持续网络监测" size="small" className="demo-network-diagnostics"
    extra={summary.sampleCount > 0 && <Tag color={summary.warningSampleCount ? 'orange' : 'default'}>
      {summary.warningSampleCount ? '检测到网络波动' : summary.measuredSampleCount ? '已采集监测数据' : '有效网络数据不足'}
    </Tag>}>
    {!summary.sampleCount ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该 Demo 暂无持续网络监测数据" /> : <>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="采样时间范围" span={2}>
          {formatTime(summary.firstAt!)} ～ {formatTime(summary.lastAt!)}
        </Descriptions.Item>
        <Descriptions.Item label="采样总数">{summary.sampleCount} 条</Descriptions.Item>
        <Descriptions.Item label="波动样本">{summary.warningSampleCount} 条</Descriptions.Item>
        <Descriptions.Item label="最高往返延迟（RTT）">{measurement(summary.maxRttMs, 'ms')}</Descriptions.Item>
        <Descriptions.Item label="最高丢包率">{measurement(summary.maxPacketLossRatio, '%', 100)}</Descriptions.Item>
        <Descriptions.Item label="最高抖动">{measurement(summary.maxJitterMs, 'ms')}</Descriptions.Item>
        <Descriptions.Item label="重连中样本">{summary.reconnectSampleCount} 条</Descriptions.Item>
      </Descriptions>
      <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 12 }}>
        持续采集老师浏览器的课堂连接情况。网络质量为差、非常差或连接中断，RTT ≥ 400 ms、丢包率 ≥ 5% 或连接重连、断开时标记波动，供人工审核参考。仅反映已收到的采样，不代表未采样时段或整节课的网络状况。
      </Typography.Paragraph>
      <Collapse size="small" items={[{
        key: 'samples', label: '查看逐次采样',
        children: <>
          <Typography.Paragraph type="secondary">
            RTT 取主连接与屏幕共享连接的最高值，丢包率取各音视频流的最高值，抖动取本地音视频发送抖动的最高值。“—”表示未获得测量，重连中样本数不等于重连次数。相对时间以课堂开始为准，开始前的采样单独标注。
          </Typography.Paragraph>
          <Table size="small" rowKey="key" columns={columns} dataSource={summary.samples}
            scroll={{ x: 1050 }}
            pagination={{ pageSize: 10, showSizeChanger: false, showTotal: total => `共 ${total} 条采样` }} />
        </>,
      }]} />
    </>}
  </Card>
}
